import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  UtensilsCrossed, 
  Calendar, 
  Clock, 
  Users, 
  CheckCircle2, 
  AlertCircle, 
  Search, 
  MapPin, 
  Building2, 
  Info,
  Phone,
  User as UserIcon,
  Sparkles,
  Bed
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/src/components/ui/card';
import { Button } from '@/src/components/ui/button';
import { Input } from '@/src/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/src/components/ui/select';
import { getSupabaseClient } from '@/src/lib/supabase';
import { useAuth } from '@/src/contexts/AuthContext';
import { ConsumerAuthModal } from '../consumer/ConsumerAuthModal';
import { toast } from 'sonner';

// ==========================================
// 1. Consumer Table Select (Restaurant Directory)
// ==========================================
export const ConsumerTableSelect = () => {
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const supabase = getSupabaseClient();
    if (!supabase) return;

    // Fetch branches configured for hospitality/dining
    supabase
      .from('branches')
      .select('*, tenants(id, business_name)')
      .eq('module_key', 'hospitality')
      .then(({ data, error }: any) => {
        if (data) {
          const uniqueTenants = Array.from(
            new globalThis.Map(
              data
                .filter((b: any) => b.tenants)
                .map((b: any) => [
                  b.tenants.id,
                  {
                    ...b.tenants,
                    business_name: b.branch_name || b.tenants.business_name,
                    branch_id: b.id,
                    address: b.address || 'Dining & Cafe',
                  },
                ]),
            ).values(),
          );
          setRestaurants(uniqueTenants);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const filtered = restaurants.filter((r) =>
    r.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 font-sans pb-10">
      {/* Top Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 p-4 sticky top-0 z-20 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/public')}
            className="p-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <UtensilsCrossed className="text-rose-500" size={20} />
              Book a Table
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Instant free table reservation at your favourite dining spots
            </p>
          </div>
        </div>

        {/* Quick link to Stay/Hotels */}
        <button
          onClick={() => navigate('/stay')}
          className="text-xs font-bold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-950/40 px-3 py-1.5 rounded-lg flex items-center gap-1.5 hover:bg-purple-100 transition-colors"
        >
          <Bed size={14} />
          Book a Stay
        </button>
      </div>

      {/* Search Input */}
      <div className="p-4 max-w-xl mx-auto">
        <div className="relative mb-4">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input
            type="text"
            placeholder="Search restaurants, cafes, places..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-11 rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-sm"
          />
        </div>

        {/* Category banner */}
        <div className="mb-4 bg-gradient-to-r from-rose-500 to-orange-500 rounded-2xl p-4 text-white shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider font-bold text-rose-100">Dine-In Reservations</p>
              <h3 className="text-lg font-extrabold mt-0.5">Skip The Wait. Book Free.</h3>
              <p className="text-xs text-rose-100 mt-1">Get instant confirmed seats with indoor, outdoor & AC preferences.</p>
            </div>
            <div className="h-12 w-12 rounded-2xl bg-white/20 flex items-center justify-center shrink-0">
              <Sparkles size={24} className="text-white" />
            </div>
          </div>
        </div>

        {/* Restaurant List */}
        {loading ? (
          <div className="py-20 flex flex-col items-center justify-center text-slate-400">
            <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-rose-500 mb-3" />
            <p className="text-sm font-medium">Finding available restaurants...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 border border-slate-200 dark:border-slate-800 text-center shadow-sm">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-rose-50 dark:bg-rose-950/50 text-rose-500 flex items-center justify-center mb-3">
              <UtensilsCrossed size={26} />
            </div>
            <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">No Restaurants Found</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-sm mx-auto">
              {searchTerm
                ? 'No dining places matched your search. Try another keyword.'
                : 'No restaurants are currently accepting online table bookings. Please check back soon!'}
            </p>
            <Button
              variant="outline"
              onClick={() => navigate('/public')}
              className="mt-4 text-xs font-bold"
            >
              Back to Home
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((rest: any) => (
              <button
                key={rest.id}
                onClick={() => navigate(`/book-table/reserve?tenant_id=${rest.id}&branch_id=${rest.branch_id || ''}`)}
                className="w-full text-left bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 hover:shadow-md hover:border-rose-300 dark:hover:border-rose-900 transition-all flex items-center justify-between gap-4 group"
              >
                <div className="flex items-center gap-3.5 min-w-0">
                  <div className="w-14 h-14 rounded-2xl bg-rose-50 dark:bg-rose-950/50 text-rose-600 dark:text-rose-400 flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                    <UtensilsCrossed size={24} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-slate-900 dark:text-slate-100 truncate text-base group-hover:text-rose-600 transition-colors">
                      {rest.business_name}
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate flex items-center gap-1 mt-0.5">
                      <MapPin size={12} className="shrink-0" />
                      {rest.address}
                    </p>
                    <div className="flex items-center gap-1.5 mt-2">
                      <span className="text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-900">
                        Free Reservation
                      </span>
                      <span className="text-[10px] font-semibold bg-rose-50 dark:bg-rose-950/50 text-rose-600 dark:text-rose-400 px-2 py-0.5 rounded-full border border-rose-200 dark:border-rose-900">
                        Instant Confirm
                      </span>
                    </div>
                  </div>
                </div>

                <div className="shrink-0">
                  <span className="text-xs font-bold text-rose-600 dark:text-rose-400 group-hover:translate-x-0.5 inline-flex items-center transition-transform">
                    Book &rarr;
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ==========================================
// 2. Consumer Table Booking Form (Flow & RPC)
// ==========================================
export const ConsumerTableBooking = () => {
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get('tenant_id');
  const branchId = searchParams.get('branch_id');
  const { user } = useAuth();
  const navigate = useNavigate();

  const [restaurantName, setRestaurantName] = useState('');
  const [restaurantAddress, setRestaurantAddress] = useState('');
  const [loadingRestaurant, setLoadingRestaurant] = useState(true);

  // Form State
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [partySize, setPartySize] = useState('2');
  const [bookingDate, setBookingDate] = useState(() => {
    // Default to today
    const now = new Date();
    return now.toISOString().split('T')[0];
  });
  const [bookingTime, setBookingTime] = useState(() => {
    // Default to nearest next hour
    const now = new Date();
    now.setHours(now.getHours() + 1, 0, 0, 0);
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });
  const [diningArea, setDiningArea] = useState('Any');
  const [specialRequest, setSpecialRequest] = useState('');

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [confirmedReservation, setConfirmedReservation] = useState<any>(null);

  // Prefill guest name and phone from current user profile
  useEffect(() => {
    if (user) {
      const metaName = user.user_metadata?.full_name || user.user_metadata?.name || '';
      if (metaName && !guestName) {
        setGuestName(metaName);
      }
      const metaPhone = user.phone || user.user_metadata?.phone || '';
      if (metaPhone && !guestPhone) {
        setGuestPhone(metaPhone);
      }
    }
  }, [user]);

  // Fetch restaurant details
  useEffect(() => {
    const supabase = getSupabaseClient();
    if (!supabase || !tenantId) {
      setLoadingRestaurant(false);
      return;
    }

    supabase
      .from('tenants')
      .select('business_name')
      .eq('id', tenantId)
      .single()
      .then(({ data }: any) => {
        if (data) setRestaurantName(data.business_name);
      })
      .catch(() => {});

    if (branchId) {
      supabase
        .from('branches')
        .select('branch_name, address')
        .eq('id', branchId)
        .single()
        .then(({ data }: any) => {
          if (data) {
            if (data.branch_name) setRestaurantName(data.branch_name);
            if (data.address) setRestaurantAddress(data.address);
          }
          setLoadingRestaurant(false);
        })
        .catch(() => setLoadingRestaurant(false));
    } else {
      setLoadingRestaurant(false);
    }
  }, [tenantId, branchId]);

  // Common time slots for quick selection
  const quickTimeSlots = [
    { label: '12:30 PM', time: '12:30' },
    { label: '1:00 PM', time: '13:00' },
    { label: '1:30 PM', time: '13:30' },
    { label: '2:00 PM', time: '14:00' },
    { label: '7:00 PM', time: '19:00' },
    { label: '7:30 PM', time: '19:30' },
    { label: '8:00 PM', time: '20:00' },
    { label: '8:30 PM', time: '20:30' },
    { label: '9:00 PM', time: '21:00' },
  ];

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    // Prompt user to log in if unauthenticated
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    if (!tenantId) {
      toast.error('Restaurant not identified. Please re-select from list.');
      return;
    }

    if (!guestName.trim()) {
      toast.error('Please enter guest name');
      return;
    }

    if (!guestPhone.trim()) {
      toast.error('Please enter phone number');
      return;
    }

    const pSize = parseInt(partySize, 10);
    if (isNaN(pSize) || pSize < 1) {
      toast.error('Party size must be at least 1 person');
      return;
    }

    // Construct full ISO timestamp
    const reservationDate = new Date(`${bookingDate}T${bookingTime}:00`);
    if (isNaN(reservationDate.getTime())) {
      toast.error('Invalid date or time selected');
      return;
    }

    // Must be a future time
    if (reservationDate.getTime() <= Date.now()) {
      const msg = 'Reservation time must be in the future. Please pick an upcoming time.';
      setErrorMessage(msg);
      toast.error(msg);
      return;
    }

    setSubmitting(true);
    const supabase = getSupabaseClient();
    if (!supabase) {
      toast.error('Database connection unavailable');
      setSubmitting(false);
      return;
    }

    try {
      const payload = {
        p_tenant_id: tenantId,
        p_branch_id: branchId || null,
        p_customer_user_id: user.id,
        p_guest_name: guestName.trim(),
        p_guest_phone: guestPhone.trim(),
        p_party_size: pSize,
        p_reserved_for: reservationDate.toISOString(),
        p_dining_area_preference: diningArea === 'Any' ? null : diningArea,
        p_special_request: specialRequest.trim() || null,
      };

      const { data, error } = await supabase.rpc('create_online_table_reservation', payload);

      if (error) {
        console.warn('create_online_table_reservation error:', error);
        // Display exact backend error message to the user as mandated in step 4
        setErrorMessage(error.message || 'No tables available for the selected time and party size.');
        toast.error(error.message || 'Reservation could not be completed.');
        return;
      }

      // Success payload mapping
      const resItem = Array.isArray(data) ? data[0] : (typeof data === 'object' && data !== null ? data : { reservation_id: data });
      const confirmedObj = {
        reservation_id: resItem.reservation_id || resItem.id || 'CONFIRMED',
        table_id: resItem.table_id || null,
        reserved_for: resItem.reserved_for || reservationDate.toISOString(),
        party_size: resItem.party_size || pSize,
        restaurant_name: restaurantName || 'Restaurant',
        dining_area: diningArea,
        guest_name: guestName.trim(),
        guest_phone: guestPhone.trim(),
      };

      setConfirmedReservation(confirmedObj);
      toast.success('Table booked successfully!');
    } catch (err: any) {
      console.error('Reservation exception:', err);
      setErrorMessage(err.message || 'Error creating reservation');
      toast.error(err.message || 'Failed to complete reservation');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDisplayDateTime = (isoOrStr: string) => {
    try {
      const d = new Date(isoOrStr);
      if (isNaN(d.getTime())) return isoOrStr;
      return d.toLocaleDateString('en-IN', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return isoOrStr;
    }
  };

  // ==========================================
  // Success Confirmation Screen
  // ==========================================
  if (confirmedReservation) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-slate-200 dark:border-slate-800 text-center overflow-hidden">
          <div className="bg-gradient-to-b from-emerald-500/10 to-transparent p-6 pb-2">
            <div className="h-16 w-16 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-3">
              <CheckCircle2 size={36} />
            </div>
            <h2 className="text-2xl font-extrabold text-slate-900 dark:text-slate-100">
              Aapki table book ho gayi hai!
            </h2>
            <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 mt-1">
              {formatDisplayDateTime(confirmedReservation.reserved_for)}, {confirmedReservation.party_size} logon ke liye.
            </p>
          </div>

          <CardContent className="p-6 pt-2 space-y-4">
            <div className="bg-slate-50 dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-800 text-left space-y-2.5 text-xs">
              <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-800">
                <span className="text-slate-500">Restaurant:</span>
                <span className="font-bold text-slate-900 dark:text-slate-100 text-sm">
                  {confirmedReservation.restaurant_name}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-slate-500">Date & Time:</span>
                <span className="font-semibold text-slate-800 dark:text-slate-200">
                  {formatDisplayDateTime(confirmedReservation.reserved_for)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-slate-500">Guests:</span>
                <span className="font-semibold text-slate-800 dark:text-slate-200">
                  {confirmedReservation.party_size} Persons
                </span>
              </div>

              {confirmedReservation.dining_area && confirmedReservation.dining_area !== 'Any' && (
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Dining Area:</span>
                  <span className="font-semibold text-slate-800 dark:text-slate-200">
                    {confirmedReservation.dining_area}
                  </span>
                </div>
              )}

              <div className="flex justify-between items-center">
                <span className="text-slate-500">Guest Name:</span>
                <span className="font-semibold text-slate-800 dark:text-slate-200">
                  {confirmedReservation.guest_name}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-slate-500">Phone:</span>
                <span className="font-mono text-slate-800 dark:text-slate-200">
                  {confirmedReservation.guest_phone}
                </span>
              </div>

              <div className="flex justify-between items-center pt-2 border-t border-slate-200 dark:border-slate-800">
                <span className="text-slate-500">Booking Charges:</span>
                <span className="font-bold text-emerald-600 dark:text-emerald-400">
                  FREE (Zero Payment)
                </span>
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <Button
                onClick={() => navigate('/public?tab=profile&view=table_bookings')}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11 rounded-xl shadow-md"
              >
                View in My Table Bookings
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/public')}
                className="w-full h-11 rounded-xl font-bold"
              >
                Back to Explore
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ==========================================
  // Main Booking Form
  // ==========================================
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 font-sans pb-16">
      {/* Top Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 p-4 sticky top-0 z-20 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/book-table')}
            className="p-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <UtensilsCrossed className="text-rose-500" size={18} />
              {restaurantName || 'Restaurant Table Reservation'}
            </h1>
            {restaurantAddress && (
              <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                <MapPin size={11} /> {restaurantAddress}
              </p>
            )}
          </div>
        </div>

        <span className="text-[11px] font-bold bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 px-2.5 py-1 rounded-full border border-emerald-200 dark:border-emerald-900">
          Free Booking
        </span>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {/* Error Alert with Suggestion (Per Requirement 4) */}
        {errorMessage && (
          <div className="p-4 rounded-2xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/60 text-red-800 dark:text-red-300 text-sm space-y-1.5 shadow-sm animate-in fade-in duration-200">
            <div className="flex items-center gap-2 font-bold text-red-900 dark:text-red-200">
              <AlertCircle size={18} className="text-red-600 shrink-0" />
              <span>Table Not Available</span>
            </div>
            <p className="leading-relaxed">{errorMessage}</p>
            <div className="pt-2 border-t border-red-200/60 dark:border-red-900/60 flex items-start gap-1.5 text-xs text-red-700 dark:text-red-300">
              <Info size={14} className="shrink-0 mt-0.5" />
              <span>
                <strong>Suggestion:</strong> Kripya thoda pehle ya baad ka time (e.g. 30 minutes aage/peeche) select karein ya party size adjust karke try karein.
              </span>
            </div>
          </div>
        )}

        <Card className="border-slate-200 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 rounded-2xl overflow-hidden">
          <CardHeader className="pb-3 border-b border-slate-100 dark:border-slate-800">
            <CardTitle className="text-base font-bold text-slate-900 dark:text-slate-100 flex items-center justify-between">
              <span>Reservation Details</span>
              <span className="text-xs font-semibold text-rose-500 font-normal">Instant Confirmation</span>
            </CardTitle>
            <CardDescription className="text-xs">
              Fill the details below to reserve your table. No advance payment required.
            </CardDescription>
          </CardHeader>

          <CardContent className="p-5">
            <form onSubmit={handleFormSubmit} className="space-y-4">
              {/* Guest Name */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">
                  Guest Name *
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <Input
                    required
                    placeholder="Enter your name"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    className="pl-10 h-11 rounded-xl"
                  />
                </div>
              </div>

              {/* Guest Phone */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">
                  Phone Number *
                </label>
                <div className="relative">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <Input
                    required
                    type="tel"
                    placeholder="10-digit mobile number"
                    value={guestPhone}
                    onChange={(e) => setGuestPhone(e.target.value)}
                    className="pl-10 h-11 rounded-xl"
                  />
                </div>
              </div>

              {/* Party Size */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Party Size (Guests) *
                  </label>
                  <span className="text-xs font-bold text-slate-700 dark:text-slate-300">
                    {partySize} {parseInt(partySize, 10) === 1 ? 'Guest' : 'Guests'}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPartySize(String(Math.max(1, (parseInt(partySize, 10) || 2) - 1)))}
                    className="h-11 w-11 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 font-bold text-lg flex items-center justify-center text-slate-700 dark:text-slate-300 transition-colors"
                  >
                    -
                  </button>
                  <Input
                    type="number"
                    min="1"
                    max="50"
                    required
                    value={partySize}
                    onChange={(e) => setPartySize(e.target.value)}
                    className="h-11 text-center font-bold text-base rounded-xl"
                  />
                  <button
                    type="button"
                    onClick={() => setPartySize(String((parseInt(partySize, 10) || 2) + 1))}
                    className="h-11 w-11 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 font-bold text-lg flex items-center justify-center text-slate-700 dark:text-slate-300 transition-colors"
                  >
                    +
                  </button>
                </div>

                {/* Quick Party Size Chips */}
                <div className="flex items-center gap-1.5 mt-2 overflow-x-auto pb-1">
                  {['1', '2', '4', '6', '8', '12'].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setPartySize(n)}
                      className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-all ${
                        partySize === n
                          ? 'bg-rose-50 border-rose-400 text-rose-600 font-bold dark:bg-rose-950/50'
                          : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'
                      }`}
                    >
                      {n} {n === '1' ? 'Person' : 'People'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Date & Time */}
              <div className="space-y-2">
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Date & Time *
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={15} />
                    <Input
                      type="date"
                      required
                      min={new Date().toISOString().split('T')[0]}
                      value={bookingDate}
                      onChange={(e) => setBookingDate(e.target.value)}
                      className="pl-9 h-11 rounded-xl text-xs sm:text-sm"
                    />
                  </div>

                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={15} />
                    <Input
                      type="time"
                      required
                      value={bookingTime}
                      onChange={(e) => setBookingTime(e.target.value)}
                      className="pl-9 h-11 rounded-xl text-xs sm:text-sm"
                    />
                  </div>
                </div>

                {/* Quick Time Slots */}
                <div>
                  <p className="text-[11px] text-slate-400 mb-1 font-medium">Quick Time Slots:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {quickTimeSlots.map((slot) => (
                      <button
                        key={slot.time}
                        type="button"
                        onClick={() => setBookingTime(slot.time)}
                        className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${
                          bookingTime === slot.time
                            ? 'bg-rose-50 border-rose-400 text-rose-600 font-bold dark:bg-rose-950/50'
                            : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300'
                        }`}
                      >
                        {slot.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Dining Area Preference */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">
                  Dining Area Preference (Optional)
                </label>
                <Select value={diningArea} onValueChange={(val) => setDiningArea(val)}>
                  <SelectTrigger className="h-11 rounded-xl">
                    <SelectValue placeholder="Select Area" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Any">No Preference / Any Area</SelectItem>
                    <SelectItem value="Indoor">Indoor (Main Hall)</SelectItem>
                    <SelectItem value="Outdoor">Outdoor / Open Air</SelectItem>
                    <SelectItem value="AC">AC Dining Area</SelectItem>
                    <SelectItem value="Rooftop">Rooftop Terrace</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Special Requests */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">
                  Special Request (Optional)
                </label>
                <Input
                  placeholder="e.g. Window seat, anniversary, high chair needed"
                  value={specialRequest}
                  onChange={(e) => setSpecialRequest(e.target.value)}
                  className="h-11 rounded-xl"
                />
              </div>

              {/* Submit CTA */}
              <div className="pt-2">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-rose-600 hover:bg-rose-700 text-white font-bold h-12 rounded-xl text-base shadow-lg shadow-rose-200 dark:shadow-none transition-all gap-2"
                >
                  {submitting ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      <span>Checking Availability & Booking...</span>
                    </div>
                  ) : (
                    <>
                      <UtensilsCrossed size={18} />
                      <span>Confirm Table Reservation (Free)</span>
                    </>
                  )}
                </Button>
                <p className="text-center text-[11px] text-slate-400 mt-2">
                  No payment needed • Your table will be held for 15 minutes past reservation time.
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Auth Modal Trigger if user not logged in */}
      {showAuthModal && (
        <ConsumerAuthModal
          onClose={() => setShowAuthModal(false)}
          onSuccess={() => setShowAuthModal(false)}
        />
      )}
    </div>
  );
};
